const u = up.util;
up.FieldWatcher = class FieldWatcher {
    constructor(root, options, callback) {
        this._options = options;
        this._root = root;
        this._callback = callback;
        this._batch = options.batch;
        this._logPrefix = options.logPrefix ?? 'up.watch()';
        this._includeDisabled = options.includeDisabled;
        this._ensureWatchable();
    }
    start() {
        this._scheduledValues = null;
        this._processedValues = this._readFieldValues();
        this._currentTimer = null;
        this._callbackRunning = false;
        return u.sequence(this._trackFields(), this._trackAbort(), this._trackReset(), () => this._abort());
    }
    _ensureWatchable() {
        const fail = (message) => up.fail(message, this._logPrefix, this._root);
        if (!this._callback) {
            fail('No callback provided for %s (%o)');
        }
        if (this._root.matches('input[type=radio]')) {
            fail('Use %s with the container of a radio group, not with an individual radio button (%o)');
        }
        if (up.form.isField(this._root) && !this._root.name) {
            fail('%s can only watch fields with a name (%o)');
        }
    }
    _trackFields() {
        if (up.form.isField(this._root)) {
            return this._watchField(this._root);
        }
        else {
            return up.form.trackFields(this._root, (field) => this._watchField(field));
        }
    }
    _trackAbort() {
        let guard = ({ target }) => target.contains(this._region);
        return up.on('up:fragment:aborted', { guard }, () => this._abort());
    }
    _trackReset() {
        let guard = ({ target }) => target === this._region;
        return up.on('reset', { guard }, (event) => this._onFormReset(event));
    }
    get _region() {
        return up.form.getRegion(this._root);
    }
    _fieldOptions(field) {
        let rootOptions = u.copy(this._options);
        return up.form.watchOptions(field, rootOptions, { defaults: { event: 'input' } });
    }
    _watchField(field) {
        let fieldOptions = this._fieldOptions(field);
        let eventType = fieldOptions.event;
        return up.on(field, eventType, (event) => this._check(event, fieldOptions));
    }
    _abort() {
        this._scheduledValues = null;
    }
    _scheduleValues(values, fieldOptions) {
        this._scheduledValues = values;
        this._scheduledFieldOptions = fieldOptions;
        let delay = fieldOptions.delay || 0;
        clearTimeout(this._currentTimer);
        this._currentTimer = u.timer(delay, () => {
            this._currentTimer = null;
            this._requestCallback();
        });
    }
    _isNewValues(values) {
        return !u.isEqual(values, this._processedValues) && !u.isEqual(this._scheduledValues, values);
    }
    async _requestCallback() {
        if (!this._scheduledValues)
            return;
        if (this._callbackRunning)
            return;
        if (this._currentTimer)
            return;
        if (!up.fragment.isAlive(this._region))
            return;
        let callbackOptions = u.omit(this._scheduledFieldOptions, ['event', 'delay']);
        const diff = this._changedValues(this._processedValues, this._scheduledValues);
        this._processedValues = this._scheduledValues;
        this._scheduledValues = null;
        this._callbackRunning = true;
        this._scheduledFieldOptions = null;
        const callbackReturnValues = [];
        if (this._batch) {
            callbackReturnValues.push(this._runCallback(diff, callbackOptions));
        }
        else {
            for (let name in diff) {
                const value = diff[name];
                callbackReturnValues.push(this._runCallback(value, name, callbackOptions));
            }
        }
        if (u.some(callbackReturnValues, u.isPromise)) {
            let callbackDone = Promise.allSettled(callbackReturnValues);
            await callbackDone;
        }
        this._callbackRunning = false;
        this._requestCallback();
    }
    _runCallback(...args) {
        return up.error.guard(this._callback, ...args);
    }
    _changedValues(previous, next) {
        const changes = {};
        let keys = Object.keys(previous);
        keys = keys.concat(Object.keys(next));
        keys = u.uniq(keys);
        for (let key of keys) {
            const previousValue = previous[key];
            const nextValue = next[key];
            if (!u.isEqual(previousValue, nextValue)) {
                changes[key] = nextValue;
            }
        }
        return changes;
    }
    _readFieldValues() {
        return up.Params.fromContainer(this._root, { includeDisabled: this._includeDisabled }).toObject();
    }
    _check(event, fieldOptions = {}) {
        const values = this._readFieldValues();
        if (this._isNewValues(values)) {
            up.log.putsEvent(event);
            this._scheduleValues(values, fieldOptions);
        }
    }
    _onFormReset(event) {
        u.task(() => this._check(event));
    }
};
